import { useEffect } from 'react';
import { RECAPTCHA_SITE_KEY } from "../Config";

declare global {
    interface Window {
        grecaptcha:any;
    }
}

// The backend (verify_recaptcha in handlers/user.py) rejects tokens minted for
// any other action.
const RECAPTCHA_ACTION = "homepage";

let scriptPromise: Promise<void> | null = null;

// Injects api.js at most once per page load, however many forms mount it.
const loadRecaptcha = (): Promise<void> => {
    if (!scriptPromise) {
        scriptPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
            script.async = true;
            script.addEventListener("load", () => window.grecaptcha.ready(() => resolve()));
            script.addEventListener("error", () => {
                // Allow a later submit to retry, e.g. after a network blip.
                script.remove();
                scriptPromise = null;
                reject(new Error("Failed to load reCAPTCHA"));
            });
            document.body.appendChild(script);
        });
    }
    return scriptPromise;
};

// v3 tokens are single-use and expire after two minutes, so mint one right
// before each request instead of holding one from page load.
export const getRecaptchaToken = async (): Promise<string> => {
    await loadRecaptcha();
    return window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION });
};

// Loads the script as soon as the form renders, so reCAPTCHA can observe the
// visitor's interaction before the token is requested on submit.
const Recaptcha = () => {
    useEffect(() => {
        loadRecaptcha().catch(() => {});
    }, []);
    return null;
}

export default Recaptcha;
